import * as R from 'remeda'

/** One colour as four bytes from 0 to 255. `a` is 255 for an opaque colour. */
export type RgbaBytes = { r: number; g: number; b: number; a: number }

export type ParsedHex = {
  /** `a` is 255 when the code has no alpha digit, as in CSS. */
  bytes: RgbaBytes
  /** True for `#rgba` and `#rrggbbaa`, the two forms that write an alpha channel. */
  hasAlpha: boolean
}

const OPAQUE = 255

// The `#` is optional, so a code pasted without it still parses.
const HEX_CODE = /^#?([\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/i

/**
 * Parses `#rgb`, `#rgba`, `#rrggbb` or `#rrggbbaa`, with or without the `#`.
 *
 * @returns `null` when any character is not a hex digit or the digit count is not 3, 4, 6
 *   or 8, so a malformed code never gives a colour
 */
export const parseHex = (input: string): ParsedHex | null => {
  const digits = HEX_CODE.exec(input.trim())?.[1]
  if (digits === undefined) {
    return null
  }

  const characters = digits.split('')
  // In `#rgb` and `#rgba`, each digit stands for a pair of the same digit.
  const pairs: string[][] =
    digits.length <= 4 ? characters.map((digit) => [digit, digit]) : R.chunk(characters, 2)
  const [r = 0, g = 0, b = 0, a = OPAQUE] = pairs.map((pair) => Number.parseInt(pair.join(''), 16))

  return { bytes: { r, g, b, a }, hasAlpha: pairs.length === 4 }
}

/**
 * True only for the 8-digit form `#rrggbbaa`; a 4-digit `#rgba` gives false. The colour
 * editor shows the opacity slider only for a value in this form.
 */
export const hasAlphaPair = (input: string): boolean => HEX_CODE.exec(input)?.[1]?.length === 8
